import { useState, useEffect } from "react";
import { C, S, G } from "../utils/theme";
import { usersAPI, commAPI } from "../utils/api";

const ADMIN_NAV = [
  { key:"dashboard", label:"Dashboard", icon:"📊" },
  { section:"ACADEMIA" },
  { key:"degrees", label:"Degrees & Programs", icon:"🎓" },
  { key:"courses", label:"Course Management", icon:"📚" },
  { key:"modules", label:"Modules & Lectures", icon:"🎬" },
  { section:"STUDENTS" },
  { key:"students", label:"Student Management", icon:"👥" },
  { key:"fees", label:"Fees Management", icon:"💰" },
  { section:"ACADEMICS" },
  { key:"assignments", label:"Assignments", icon:"📝" },
  { key:"exams", label:"Exams & MCQs", icon:"📋" },
  { key:"grades", label:"Grades & Reports", icon:"🏆" },
  { section:"COMMUNICATION" },
  { key:"announcements", label:"Announcements", icon:"📢" },
  { key:"qa_admin", label:"Q&A Management", icon:"💬" },
];

const STUDENT_NAV = [
  { key:"dashboard", label:"Dashboard", icon:"📊" },
  { section:"LEARNING" },
  { key:"my_courses", label:"My Courses", icon:"📚" },
  { key:"lectures_s", label:"Lectures", icon:"🎬" },
  { section:"TASKS" },
  { key:"assignments_s", label:"Assignments", icon:"📝" },
  { key:"exams_s", label:"Exams", icon:"📋" },
  { section:"RESULTS" },
  { key:"grades_s", label:"My Grades", icon:"🏆" },
  { section:"CONNECT" },
  { key:"announcements_s", label:"Announcements", icon:"📢" },
  { key:"qa_s", label:"Q&A / AI Help", icon:"🤖" },
  { section:"RECREATION" },
  { key:"games", label:"Games", icon:"🎮" },
];

const LABELS = {
  dashboard:"Dashboard", degrees:"Degrees & Programs", courses:"Course Management",
  modules:"Modules & Lectures", students:"Student Management", fees:"Fees Management",
  assignments:"Assignments", exams:"Exams & MCQs", grades:"Grades & Reports",
  announcements:"Announcements", qa_admin:"Q&A Management",
  my_courses:"My Courses", lectures_s:"My Lectures", assignments_s:"My Assignments",
  exams_s:"My Exams", grades_s:"My Grades", announcements_s:"Announcements",
  qa_s:"Q&A / AI Help", games:"🎮 Games",
};

export default function Layout({ user, onLogout, page, setPage, children }) {
  const [showNotif, setShowNotif] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const nav = user.role === "admin" ? ADMIN_NAV : STUDENT_NAV;

  // Load notifications
  useEffect(() => {
    const loadNotifs = async () => {
      try {
        const res = await usersAPI.getNotifications();
        setNotifications(res.data || []);
      } catch (err) {}
    };
    loadNotifs();
    // Refresh every 30 seconds
    const interval = setInterval(loadNotifs, 30000);
    return () => clearInterval(interval);
  }, []);

  const unread = notifications.filter(n => !n.is_read).length;

  const markAllRead = async () => {
    try {
      await usersAPI.markAllRead();
      setNotifications(prev => prev.map(n => ({ ...n, is_read: 1 })));
    } catch (err) {}
  };

  return (
    <div style={S.layout}>
      {/* Sidebar */}
      <div style={S.sidebar}>
        {/* Logo */}
        <div style={{ padding:"24px 22px 18px", borderBottom:"1px solid rgba(255,255,255,0.06)" }}>
          <div style={{ display:"flex", alignItems:"center", gap:12 }}>
            <img src="/logo.svg" alt="MLK University" style={{ width:42, height:42, objectFit:"contain" }} />
            <div>
              <div style={{ fontSize:15, fontWeight:800, color:C.white, letterSpacing:-0.3 }}>Martin Luther King University</div>
              <div style={{ fontSize:10, color:"#475569", textTransform:"uppercase", letterSpacing:1 }}>LMS System</div>
            </div>
          </div>
        </div>

        {/* User Info */}
        <div style={{ margin:"12px 12px 4px", padding:"12px 14px", background:"rgba(255,255,255,0.05)", borderRadius:12, border:"1px solid rgba(255,255,255,0.06)" }}>
          <div style={{ display:"flex", alignItems:"center", gap:10 }}>
            <div style={{ width:38, height:38, borderRadius:"50%", background:user.role==="admin"?G.blue:G.purple, display:"flex", alignItems:"center", justifyContent:"center", fontSize:16, flexShrink:0, overflow:"hidden" }}>
              {user.photo
                ? <img src={user.photo} alt="" style={{ width:"100%", height:"100%", objectFit:"cover" }} />
                : (user.role==="admin" ? "👨‍💼" : "👨‍🎓")
              }
            </div>
            <div style={{ flex:1, minWidth:0 }}>
              <div style={{ fontSize:13, fontWeight:700, color:C.white, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{user.name}</div>
              <div style={{ fontSize:10, color:"#475569", marginTop:1 }}>
                {user.role==="admin" ? "Administrator" : user.student_id || user.email}
              </div>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav style={{ flex:1, padding:"8px 10px", overflowY:"auto" }}>
          {nav.map((item, i) =>
            item.section
              ? <div key={i} style={{ fontSize:9, fontWeight:700, letterSpacing:1.5, color:"#334155", padding:"14px 12px 4px", textTransform:"uppercase" }}>{item.section}</div>
              : <button key={item.key} onClick={() => setPage(item.key)} style={{
                  ...S.btn("ghost","md"),
                  width:"100%", justifyContent:"flex-start", padding:"10px 14px",
                  borderRadius:10, marginBottom:2, fontSize:13,
                  background: page===item.key ? "rgba(59,130,246,0.15)" : "transparent",
                  color: page===item.key ? "#3B82F6" : "#64748B",
                  borderLeft: page===item.key ? "3px solid #3B82F6" : "3px solid transparent",
                  fontWeight: page===item.key ? 700 : 400,
                }}>
                  <span style={{ fontSize:15, marginRight:2 }}>{item.icon}</span> {item.label}
                </button>
          )}
        </nav>

        {/* Logout */}
        <div style={{ padding:"12px 10px", borderTop:"1px solid rgba(255,255,255,0.05)" }}>
          <button onClick={onLogout} style={{ ...S.btn("glass","md"), width:"100%", justifyContent:"center", fontSize:13, color:"#64748B" }}>
            🚪 Sign Out
          </button>
        </div>
      </div>

      {/* Main */}
      <div style={S.mainArea}>
        {/* Topbar */}
        <div style={S.topbar}>
          <div>
            <h1 style={{ fontSize:18, fontWeight:700, color:C.white, margin:0 }}>{LABELS[page]||"Dashboard"}</h1>
            <div style={{ fontSize:11, color:"#475569", marginTop:2 }}>
              {new Date().toLocaleDateString("en-PK",{weekday:"long",day:"numeric",month:"long",year:"numeric"})}
            </div>
          </div>
          <div style={{ display:"flex", alignItems:"center", gap:16 }}>
            {/* Fees warning */}
            {user.role==="student" && user.fees_status==="unpaid" && (
              <div style={{ padding:"6px 14px", background:"rgba(244,63,94,0.15)", border:"1px solid rgba(244,63,94,0.3)", borderRadius:8, fontSize:12, color:"#F43F5E", fontWeight:600 }}>
                ❌ Fees Overdue
              </div>
            )}
            {user.role==="student" && user.fees_status==="partial" && (
              <div style={{ padding:"6px 14px", background:"rgba(245,158,11,0.15)", border:"1px solid rgba(245,158,11,0.3)", borderRadius:8, fontSize:12, color:"#F59E0B", fontWeight:600 }}>
                ⚠️ Partial Payment
              </div>
            )}

            {/* Notifications */}
            <div style={{ position:"relative" }}>
              <button
                style={{ background:"rgba(255,255,255,0.06)", border:"1px solid rgba(255,255,255,0.08)", borderRadius:10, padding:"8px 12px", cursor:"pointer", color:C.white, fontSize:16, position:"relative" }}
                onClick={() => setShowNotif(v => !v)}>
                🔔
                {unread > 0 && (
                  <span style={{ position:"absolute", top:-4, right:-4, background:"#F43F5E", color:C.white, borderRadius:"50%", width:17, height:17, fontSize:9, display:"flex", alignItems:"center", justifyContent:"center", fontWeight:700 }}>
                    {unread > 9 ? "9+" : unread}
                  </span>
                )}
              </button>

              {/* Notification Panel */}
              {showNotif && (
                <div style={{ position:"absolute", right:0, top:52, width:360, background:"#1E293B", border:"1px solid rgba(255,255,255,0.1)", borderRadius:16, boxShadow:"0 20px 60px rgba(0,0,0,0.5)", zIndex:300, overflow:"hidden" }}>
                  <div style={{ padding:"16px 20px", borderBottom:"1px solid rgba(255,255,255,0.06)", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                    <span style={{ fontWeight:700, fontSize:15, color:C.white }}>
                      Notifications {unread > 0 && <span style={{ background:"#F43F5E", color:C.white, borderRadius:20, padding:"2px 8px", fontSize:11, marginLeft:6 }}>{unread}</span>}
                    </span>
                    {unread > 0 && (
                      <button onClick={markAllRead} style={{ ...S.btn("ghost","sm"), fontSize:12, color:"#64748B" }}>
                        Mark all read
                      </button>
                    )}
                  </div>
                  <div style={{ maxHeight:380, overflowY:"auto" }}>
                    {notifications.length === 0
                      ? <div style={{ padding:28, textAlign:"center", color:"#475569", fontSize:13 }}>No notifications</div>
                      : notifications.map(n => (
                        <div key={n.id} style={{ padding:"13px 20px", borderBottom:"1px solid rgba(255,255,255,0.04)", background:n.is_read ? "transparent" : "rgba(59,130,246,0.08)", display:"flex", gap:12 }}>
                          <span style={{ fontSize:18, marginTop:1 }}>
                            {n.type==="fees" ? "💰" : n.type==="exam" ? "📋" : n.type==="assignment" ? "📝" : "📢"}
                          </span>
                          <div style={{ flex:1 }}>
                            <div style={{ fontSize:13, color:"#CBD5E1", lineHeight:1.5 }}>{n.message}</div>
                            <div style={{ fontSize:11, color:"#475569", marginTop:3 }}>
                              {new Date(n.created_at).toLocaleDateString("en-PK")}
                            </div>
                          </div>
                          {!n.is_read && <div style={{ width:8, height:8, borderRadius:"50%", background:"#3B82F6", marginTop:5, flexShrink:0 }} />}
                        </div>
                      ))
                    }
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Content */}
        <div style={S.content}>{children}</div>
      </div>

      {showNotif && <div style={{ position:"fixed", inset:0, zIndex:250 }} onClick={() => setShowNotif(false)} />}
    </div>
  );
}